import type { FC } from 'react';
import React from 'react';

import { Box, Typography, useTheme } from '@mui/material';

import { ArrowLeftIcon, CrownPlatinumIcon } from '@setup/svg-icons/svg-icons';
import { useBreakpoints } from '@shared/hooks/useBreakpoints';
import { MuiTooltip } from '@shared/ui-kit';
import {
  OptionsCardContainerV3,
  OptionsCardIconContainerV3,
} from '@shared/ui-kit/cards/OptionsCard/OptionCardV3.style';

import {
  OptionsCardContainer,
  OptionsCardIconContainer,
  OptionsCardRightIconContainer,
} from './OptionsCard.style';
import { OptionsCardContainerV2, OptionsCardIconContainerV2 } from './OptionsCardV2.style';

type Version = 'v1' | 'v2' | 'v3';

type CreateVariantItemProps = {
  item: {
    label: string;
    customLabel?: React.ReactNode;
    id: string;
    icon?: any;
    leftContent?: React.ReactNode;
    backgroundCardColor?: string;
    hoverBackgroundCardColor?: string;
    isDisabled?: boolean;
    tooltipText?: string;
    subtitle?: string;
    isSelected?: boolean;
  };

  callback: () => void;
  subtitle?: string | null;
  version?: Version;
  isPlatinum?: boolean | null;
};

export const OptionsCard: FC<CreateVariantItemProps> = ({
  item,
  callback,
  version = 'v1',
  subtitle = null,
  isPlatinum = true,
}) => {
  const theme = useTheme();

  const { isMobile } = useBreakpoints();

  function renderSwitchVersion(versions: Version) {
    switch (versions) {
      case 'v1':
        return (
          <OptionsCardContainer
            id={item.id}
            hoverBackground={item.hoverBackgroundCardColor}
            background={item.backgroundCardColor}
            onClick={callback}
            isSelected={item.isSelected}
          >
            {item.leftContent ? (
              <Box sx={{ mr: '16px' }}>{item.leftContent}</Box>
            ) : item.icon ? (
              <OptionsCardIconContainer className="variant-icon-container">
                <item.icon />
              </OptionsCardIconContainer>
            ) : (
              <></>
            )}

            {item?.customLabel ? (
              <Box maxWidth={'64%'}>{item?.customLabel}</Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="s2" sx={{ color: theme.palette.case.neutral.n700 }}>
                  {item?.label}
                </Typography>

                {!!item.subtitle && (
                  <Typography
                    variant={isMobile ? 't12r' : 't14r'}
                    sx={{
                      color: item.isDisabled
                        ? theme.palette.case.neutral.n400
                        : theme.palette.case.neutral.n500,
                      textAlign: 'start',
                    }}
                  >
                    {item.subtitle}
                  </Typography>
                )}
              </Box>
            )}
            <OptionsCardRightIconContainer
              sx={{
                svg: {
                  transform: 'rotate(180deg)',
                },
              }}
            >
              <ArrowLeftIcon />
            </OptionsCardRightIconContainer>
          </OptionsCardContainer>
        );
      case 'v2':
        return (
          <MuiTooltip
            title={item.tooltipText || ''}
            placement="right"
            isUseCustomStatement={!item.isDisabled}
          >
            <OptionsCardContainerV2 id={item.id} onClick={callback} isDisabled={item.isDisabled}>
              <OptionsCardIconContainerV2 isDisabled={item.isDisabled}>
                {item.icon ? <item.icon /> : <></>}
              </OptionsCardIconContainerV2>
              <Box display="flex" flexDirection="column" alignItems="flex-start" gap="2px">
                <Typography
                  variant="t16r"
                  sx={{
                    color: item.isDisabled
                      ? theme.palette.case.neutral.n400
                      : theme.palette.case.neutral.n800,
                  }}
                >
                  {item?.label}
                </Typography>
                {subtitle !== null && (
                  <Typography
                    variant="t14r"
                    sx={{
                      color: item.isDisabled
                        ? theme.palette.case.neutral.n400
                        : theme.palette.case.neutral.n600,
                      textAlign: 'start',
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
                {!isPlatinum && (
                  <Box position="absolute" right="12px" top="22px">
                    <CrownPlatinumIcon />
                  </Box>
                )}
              </Box>
            </OptionsCardContainerV2>
          </MuiTooltip>
        );
      case 'v3':
        return (
          <OptionsCardContainerV3 id={item.id} onClick={callback} isDisabled={item.isDisabled}>
            {item.leftContent ? (
              <Box sx={{ mr: '8px' }}>{item.leftContent}</Box>
            ) : (
              <OptionsCardIconContainerV3 isDisabled={item.isDisabled}>
                {item.icon ? <item.icon /> : <></>}
              </OptionsCardIconContainerV3>
            )}

            <Box display="flex" alignItems="flex-start">
              <Typography
                variant="t16r"
                sx={{
                  color: item.isDisabled ? theme.palette.case.neutral.n400 : theme.palette.case.neutral.n800,
                }}
              >
                {item?.label}
              </Typography>
            </Box>
          </OptionsCardContainerV3>
        );
      default: {
        return 'v1';
      }
    }
  }

  return <>{renderSwitchVersion(version)}</>;
};
